import React from 'react';
import { FiBox, FiAlertTriangle, FiDollarSign, FiArrowDownCircle, FiArrowUpCircle } from 'react-icons/fi';
import '../../styles/Dashboard.css';

const fmtS = n => `S/ ${parseFloat(n || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtQ = n => parseFloat(n || 0).toLocaleString('es-PE', { maximumFractionDigits: 2 });
const fmtDt = ts => ts ? new Date(ts).toLocaleString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'America/Lima' }) : '—';

const TIPO_LABEL = {
  ingreso: 'INGRESO', salida: 'SALIDA', purchase: 'COMPRA',
  sale: 'VENTA', transferencia: 'TRANSFER.', conversion: 'CONVERSIÓN',
};

function groupByWarehouse(rows) {
  const map = {};
  rows.forEach(r => {
    if (!map[r.warehouse_id]) map[r.warehouse_id] = { warehouse_id: r.warehouse_id, warehouse_name: r.warehouse_name, items: [] };
    map[r.warehouse_id].items.push(r);
  });
  return Object.values(map).sort((a, b) => b.items.length - a.items.length);
}

function KpiCard({ icon, label, value, color }) {
  const colors = {
    blue:   { bg: '#eff6ff', border: '#bfdbfe', icon: '#3b82f6' },
    red:    { bg: '#fef2f2', border: '#fecaca', icon: '#dc2626' },
    green:  { bg: '#f0fdf4', border: '#bbf7d0', icon: '#16a34a' },
  };
  const c = colors[color] || colors.blue;
  return (
    <div className="dsh-kpi-card" style={{ background: c.bg, borderColor: c.border }}>
      <div className="dsh-kpi-icon" style={{ color: c.icon }}>{icon}</div>
      <div>
        <div className="dsh-kpi-value">{value}</div>
        <div className="dsh-kpi-label">{label}</div>
      </div>
    </div>
  );
}

export default function AlmaceneroDashboard({ data }) {
  const { kpis, stockCritico, movimientosRecientes } = data;

  return (
    <div className="dsh-container">
      <div className="dsh-header">
        <div>
          <h1 className="dsh-title">Inventario</h1>
          <p className="dsh-subtitle">Vista de almacén — productos y movimientos de stock</p>
        </div>
      </div>

      <div className="dsh-kpi-grid dsh-kpi-grid-3">
        <KpiCard icon={<FiBox />} color="blue" label="Productos Activos" value={kpis.productosActivos} />
        <KpiCard icon={<FiAlertTriangle />} color="red" label="Stock Bajo Mínimo" value={kpis.stockBajoMinimo} />
        <KpiCard icon={<FiDollarSign />} color="green" label="Valorizado (costo)" value={fmtS(kpis.valorizado)} />
      </div>

      <section className="dsh-section">
        <div className="dsh-section-title">
          <span className="dsh-dot" /> PRODUCTOS EN STOCK CRÍTICO
        </div>
        {stockCritico.length === 0 ? (
          <div className="dsh-empty">Sin productos en stock crítico</div>
        ) : groupByWarehouse(stockCritico).map(group => (
          <div key={group.warehouse_id} className="dsh-wh-group">
            <div className="dsh-wh-group-title">
              {group.warehouse_name}
              <span className="dsh-count-badge">{group.items.length}</span>
            </div>
            <div className="dsh-table-wrap">
              <table className="dsh-table">
                <thead>
                  <tr>
                    <th>SKU</th><th>Producto</th><th>Familia</th><th>U.M.</th>
                    <th style={{ textAlign: 'right' }}>Stock</th>
                    <th style={{ textAlign: 'right' }}>Mínimo</th>
                    <th style={{ textAlign: 'right' }}>Déficit</th>
                  </tr>
                </thead>
                <tbody>
                  {group.items.map(p => (
                    <tr key={`${p.id}-${p.warehouse_id}`}>
                      <td className="dsh-mono">{p.sku}</td>
                      <td style={{ fontWeight: 600 }}>{p.name}</td>
                      <td>{p.family || '—'}</td>
                      <td>{p.unit_measure || '—'}</td>
                      <td style={{ textAlign: 'right' }}>{fmtQ(p.stock)}</td>
                      <td style={{ textAlign: 'right' }}>{fmtQ(p.min_stock)}</td>
                      <td style={{ textAlign: 'right', color: '#dc2626', fontWeight: 700 }}>{fmtQ(p.deficit)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </section>

      <section className="dsh-section">
        <div className="dsh-section-title">
          <span className="dsh-dot" /> MOVIMIENTOS RECIENTES
        </div>
        <div className="dsh-table-wrap">
          <table className="dsh-table">
            <thead>
              <tr>
                <th>Fecha</th><th>Almacén</th><th>Producto</th><th>Tipo</th>
                <th style={{ textAlign: 'right' }}>Cantidad</th><th>Usuario</th>
              </tr>
            </thead>
            <tbody>
              {movimientosRecientes.length === 0 ? (
                <tr><td colSpan={6} className="dsh-empty">Sin movimientos recientes</td></tr>
              ) : movimientosRecientes.map(m => {
                const isIn = ['ingreso', 'purchase'].includes(m.movement_type) || ['TRANSFERENCIA ENTRADA', 'CONVERSION ENTRADA'].includes(m.causa);
                return (
                  <tr key={m.id}>
                    <td style={{ whiteSpace: 'nowrap', fontSize: 11.5 }}>{fmtDt(m.moved_at)}</td>
                    <td>{m.warehouse_name || '—'}</td>
                    <td style={{ fontWeight: 600 }}>{m.product_name}</td>
                    <td>
                      <span className={`dsh-move-badge ${isIn ? 'dsh-move-in' : 'dsh-move-out'}`}>
                        {isIn ? <FiArrowDownCircle size={11} /> : <FiArrowUpCircle size={11} />}
                        {TIPO_LABEL[m.movement_type] || m.movement_type}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: isIn ? '#16a34a' : '#dc2626' }}>
                      {isIn ? '+' : '-'}{fmtQ(m.qty_moved)}
                    </td>
                    <td style={{ fontSize: 11.5, color: '#64748b' }}>{m.username || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
